/**
 * The brokerage's compliance documents (PR B, V062).
 *
 * This file also held a second, flat brokerage-profile form used only by the
 * platform admin's tenant screen — the same fields a broker edits on My
 * brokerage, in a different and much more cramped layout (nine inputs on one
 * row). Two forms over one row is how they drift, and the worse of the two was
 * the one you provision a new brokerage from. `BrokerageProfile` in
 * MyBrokerage.tsx is now the single form, mounted for either caller; what is
 * left here is the compliance section it embeds.
 */

import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  complianceDocsService,
  uploadComplianceDocFile,
} from "@re-platform/api-client";
import { api } from "../../lib/api";

/**
 * The brokerage's own compliance documents (PR B, V062) — its IABS (required
 * before a REAL_ESTATE tenant can activate) and an optional CPN override
 * (the official TREC form is used otherwise). agent_id omitted = the
 * brokerage's own document, never an individual agent's.
 */
export function ComplianceDocsSection({ tenantId }: { tenantId?: string }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const iabsInputRef = useRef<HTMLInputElement>(null);
  const cpnInputRef = useRef<HTMLInputElement>(null);
  const target = tenantId ? { tenantId } : undefined;
  const queryKey = ["compliance-docs", "brokerage", tenantId ?? "own"];

  const q = useQuery({
    queryKey,
    queryFn: () => complianceDocsService.list(api, target),
  });

  const upload = useMutation({
    mutationFn: async ({
      file,
      kind,
    }: {
      file: File;
      kind: "IABS" | "CPN";
    }) => {
      const presigned = await complianceDocsService.presignUpload(
        api,
        kind,
        target,
      );
      await uploadComplianceDocFile(presigned.upload_url, file);
      return complianceDocsService.confirmUpload(
        api,
        presigned.s3_key,
        kind,
        target,
      );
    },
    onSuccess: (_saved, { kind }) => {
      void queryClient.invalidateQueries({ queryKey });
      setToast(`${kind} uploaded.`);
    },
    onError: (err: Error) => setToast(err.message),
  });

  const hasIabs = q.data?.some((d) => d.kind === "IABS") ?? false;
  const hasCpn = q.data?.some((d) => d.kind === "CPN") ?? false;

  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ fontSize: 13, marginBottom: 4 }}>Compliance documents</h3>
      <p
        style={{
          fontSize: 12,
          color: "var(--rp-color-text-muted)",
          marginTop: 0,
        }}
      >
        The brokerage's own Information About Brokerage Services (IABS) — a
        REAL_ESTATE tenant cannot activate without one. A Consumer Protection
        Notice override is optional; the official TREC form is used when none is
        uploaded.
      </p>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <div>
          <input
            ref={iabsInputRef}
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ file, kind: "IABS" });
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="rp-btn rp-btn--sm"
            disabled={upload.isPending}
            onClick={() => iabsInputRef.current?.click()}
          >
            {hasIabs ? "Replace IABS" : "Upload IABS"}
          </button>
          {!q.isLoading && !hasIabs && (
            <div style={{ fontSize: 11, color: "var(--rp-color-danger)" }}>
              Missing — required to activate.
            </div>
          )}
        </div>
        <div>
          <input
            ref={cpnInputRef}
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ file, kind: "CPN" });
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="rp-btn rp-btn--sm"
            disabled={upload.isPending}
            onClick={() => cpnInputRef.current?.click()}
          >
            {hasCpn ? "Replace CPN override" : "Upload CPN override (optional)"}
          </button>
        </div>
      </div>
      {toast && (
        <p style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
          {toast}
        </p>
      )}
    </div>
  );
}
